import { useProPilotStore, useProPilotStoreActions } from "PFApp/stores/propilot_store";
import { ProPilotMessageStatus } from "PFApp/stores/propilot_store";
import { Button } from "PFComponents/button/button";
import { Inline } from "PFComponents/containers";
import { EventKey } from "PFTypes/event_key";
import { KeyboardEvent, useEffect, useId, useRef } from "react";
import { useTranslation } from "react-i18next";

import css from "./propilot_input.module.scss";
import { useAutoResizeTextarea } from "./use_auto_resize_textarea";

type ProPilotInputProps = {
  conversationId: string;
  status: ProPilotMessageStatus;
  allowFreeText: boolean;
  isConnected: () => boolean;
  onSubmit: (text: string) => void;
};

export const ProPilotInput = ({
  conversationId,
  status,
  allowFreeText,
  isConnected,
  onSubmit
}: ProPilotInputProps) => {
  const { t } = useTranslation("translation");
  const value = useProPilotStore((state) => state.conversations[conversationId]?.userInput.value ?? "");
  const { setUserInputValue } = useProPilotStoreActions();
  const labelId = useId();
  const hintId = useId();
  const textareaRef = useAutoResizeTextarea(value);

  const isSendDisabled = status === "sending" || status === "streaming" || !allowFreeText || !isConnected();
  const isInputDisabled = !allowFreeText;

  // Guarded so we never steal focus on mount or panel-open.
  const refocusPendingRef = useRef(false);

  useEffect(() => {
    if (!isSendDisabled && refocusPendingRef.current) {
      refocusPendingRef.current = false;
      textareaRef.current?.focus();
    }
  }, [isSendDisabled, textareaRef]);

  const handleSubmit = () => {
    const trimmed = value.trim();
    if (!trimmed || isSendDisabled) {
      return;
    }
    refocusPendingRef.current = true;
    onSubmit(trimmed);
    setUserInputValue(conversationId, "");
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === EventKey.Enter && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    } else if (e.key === EventKey.Escape) {
      if (value !== "") {
        e.stopPropagation();
        setUserInputValue(conversationId, "");
      }
    }
  };

  const busyHint = t("propilot.input.disabledBusy");
  const freeTextHint = t("propilot.input.disabledFreeText");

  return (
    <Inline alignItems="center" gap="spacingLg" className={css.input} data-qa-id="ProPilotInput">
      <label id={labelId} htmlFor={`${labelId}-textarea`} className={css.visuallyHidden}>
        {t("propilot.input.label")}
      </label>
      {(isSendDisabled || isInputDisabled) && (
        <span id={hintId} className={css.visuallyHidden}>
          {isInputDisabled ? freeTextHint : busyHint}
        </span>
      )}
      <Inline alignItems="center" className={css.inputContainer}>
        <textarea
          id={`${labelId}-textarea`}
          ref={textareaRef}
          className={css.textarea}
          value={value}
          disabled={isInputDisabled}
          aria-labelledby={labelId}
          aria-describedby={isInputDisabled ? hintId : undefined}
          placeholder={t("propilot.input.placeholder")}
          rows={1}
          data-qa-id="ProPilotInput.textarea"
          onChange={(e) => setUserInputValue(conversationId, e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <Button
          icon="paper-plane"
          kind="primary"
          small
          className={css.submitButton}
          disabled={isSendDisabled || value.trim() === ""}
          aria-describedby={isSendDisabled && !isInputDisabled ? hintId : undefined}
          title={t("propilot.input.submit")}
          qaId="ProPilotInput.submit"
          onClick={handleSubmit}
        />
      </Inline>
    </Inline>
  );
};
